/// <reference types="google.maps" />

import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter, Input, ViewChild, ElementRef, signal, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { first, skip } from 'rxjs/operators';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { MapService } from 'src/app/services/map/map.service';
import { MapPoiDrawerService } from 'src/app/services/map/map-poi-drawer.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { SearchedRouteElement } from 'src/app/models/searched-route-element';
import { SettingsService } from 'src/app/services/account/settings.service';
import { AccountService } from 'src/app/services/account/account.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { Address } from 'src/app/models/address';
import { SettingsDialogComponent } from 'src/app/components/modals/settings-dialog/settings-dialog.component';
import { AccountDialogComponent } from 'src/app/components/modals/account-dialog/account-dialog.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { getTextSegments, StoredSearch, storeLastSearch, syncSearchHistory, TextSegment } from './search.utils';
declare let google;

const LAST_SEARCHED_KEY = 'lastSearchedLocations';

@Component({
  selector: 'app-search',
  standalone: true,
  templateUrl: './search.component.html',
  imports: [CommonModule, FormsModule],
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {
  @Input() routeIcon: string = "";
  @Input() inputValue: string = '';
  @Input() waypointIdx: number = 0;
  @Input() failedPlan: boolean = false;
  @Input() addressSearch: boolean = false;
  @Input() batteryReservedWarning: boolean = false;
  @Input() csid: string = '';
  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef<HTMLInputElement>;
  @Output() searchChanged: EventEmitter<any> = new EventEmitter();
  @Output() keyPressed: EventEmitter<any> = new EventEmitter();
  @Output() goBackEvent: EventEmitter<any> = new EventEmitter();
  @Output() searchActiveChanged: EventEmitter<boolean> = new EventEmitter();
  public readonly searchActive = signal<boolean>(false);
  private searchDropdownListener: any;
  private readonly firstClick = fromEvent(document, 'click').pipe(first());
  private readonly documentClick = fromEvent(document, 'click').pipe(skip(1));
  private searchInterval: ReturnType<typeof setTimeout> | undefined;
  private readyToFocusText: boolean = true;
  private sessionToken!: google.maps.places.AutocompleteSessionToken;
  public readonly displayedSearchResults = signal<google.maps.places.AutocompleteSuggestion[]>([]);
  public readonly searchHistory = signal<SearchedRouteElement[]>([]);
  public readonly isAuthorized = signal<boolean>(false);
  private authSubscription!: Subscription;
  private readonly languageSubscription!: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef, public mobileResolutionService: MobileResolutionService,
    private readonly mapService: MapService, public languageService: LanguageService, private readonly consoleLoggerService: ConsoleLoggerService,
    public settingsService: SettingsService, private readonly mapPoiDrawerService: MapPoiDrawerService,
    private readonly accountService: AccountService, private readonly stackedModalsService: StackedModalsService,
    private readonly webviewService: WebviewService) {
  }

  ngOnInit(): void {
    if (!google?.maps?.places) {
      console.error('Google maps library not loaded');
      return;
    }

    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    this.searchHistory.set(this.getLastSearches());
    this.syncSearchHistory();

    this.authSubscription = this.accountService.ObservableIsAuthorized.subscribe((resp) => {
      this.isAuthorized.set(resp === true);
    });

  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    // A waypoint row can be torn down while its dropdown is open (the list is
    // rebuilt after a pick), and the parent must not keep treating it as open.
    this.setSearchActive(false);
    if (this.searchDropdownListener) {
      this.searchDropdownListener.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  /**
   * The dropdown is opened and closed from several places (focus, typing,
   * outside click, picking a result), so the parent is told about each
   * change from this one spot.
   */
  private setSearchActive(active: boolean): void {
    if (this.searchActive() === active) {
      return;
    }
    this.searchActive.set(active);
    this.searchActiveChanged.emit(active);
  }

  public get showHomeWork(): boolean {
    return !this.addressSearch;
  }

  public get homeAddressDescription(): string {
    return this.isAuthorized() ? (this.settingsService.getHomeAddress()?.Description ?? '') : '';
  }

  public get workAddressDescription(): string {
    return this.isAuthorized() ? (this.settingsService.getWorkAddress()?.Description ?? '') : '';
  }

  // wait 1s after last keypress
  public async onSearchSubmit(value: string): Promise<void> {
    if (value != null && value != '') {
      clearTimeout(this.searchInterval);
      this.searchInterval = setTimeout(() => {
        this.getSearchedList(value);
      }, 1000);
    }
    else {
      this.displayedSearchResults.set([]);
      this.setSearchActive(false);
    }
  }

  // get map search results
  private async getSearchedList(value: string): Promise<void> {
    const mapBounds = this.mapService.getMapBounds();
    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(mapBounds.south, mapBounds.west),
      new google.maps.LatLng(mapBounds.north, mapBounds.east));

    let request = {
      input: value,
      sessionToken: this.sessionToken,
      locationBias: bounds
    };

    const result = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    this.displayedSearchResults.set(result.suggestions);
    this.setSearchActive(true);

    //create observable that emits click events
    if (this.searchDropdownListener) {
      this.searchDropdownListener.unsubscribe();
    }

    this.keyPressed.emit();

    this.searchDropdownListener = this.firstClick.subscribe(val => {
      this.setSearchActive(false);
    });
  }

  public searchActivated(): void {
    if (this.readyToFocusText) {
      this.readyToFocusText = false;
      this.searchInput.nativeElement.focus();
      this.searchInput.nativeElement.setSelectionRange(0, this.searchInput.nativeElement.value.length);

      this.searchDropdownListener = this.documentClick.subscribe((val: any) => {
        if (val.target != this.searchInput.nativeElement) {
          this.readyToFocusText = true;
          this.searchDropdownListener.unsubscribe();
        }
      });
    }
  }

  public searchFocused(): void {
    // Android Auto may have added searches while this page stayed loaded.
    this.syncSearchHistory();
    if (this.showHomeWork || this.searchHistory()?.length > 0) {
      this.setSearchActive(true);

      this.searchDropdownListener = this.documentClick.subscribe((val: any) => {
        if (val.target != this.searchInput.nativeElement) {
          this.setSearchActive(false);
        }
      });
    }
  }

  public searchInputChanged(value: string): void {
    if (value != null && value != "") {
      this.onSearchSubmit(value);
    }
    else {
      // Text cleared: cancel any pending search and drop stale results so the
      // dropdown falls back to the search history instead of the last results.
      clearTimeout(this.searchInterval);
      this.displayedSearchResults.set([]);

      const hasDropdownContent = this.showHomeWork || this.searchHistory()?.length > 0;
      this.setSearchActive(hasDropdownContent);
    }
  }

  public async searchElementSelected(searchEl: any): Promise<void> {
    if (searchEl.placePrediction?.placeId) {
      const place = new google.maps.places.Place({
        id: searchEl.placePrediction.placeId
      });

      await place.fetchFields({ fields: ["location", "viewport"] });

      const lat = place.location!.lat();
      const lng = place.location!.lng();
      const label = searchEl.placePrediction?.text?.text || '';
      const ne = place.viewport.getNorthEast();
      const sw = place.viewport.getSouthWest();

      const boundingbox = [
        ne.lat(),
        ne.lng(),
        sw.lat(),
        sw.lng()
      ];
      this.inputValue = label;

      const selected: SearchedRouteElement = {
        display_name: label,
        lat: lat,
        lon: lng,
        boundingbox: boundingbox
      };
      this.searchChanged.emit(selected);
      this.storeLastSearch(selected);

      // In address-search mode the parent address panel owns the map card
      // (read-only, no GO button), so skip drawing the default POI here.
      if (!this.routeIcon && !this.addressSearch) {
        this.mapService.centerMap(15, lat, lng);
        this.mapPoiDrawerService.drawPoi(lat, lng, { address: label });
      }

      this.setSearchActive(false);
      this.displayedSearchResults.set([]);
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
  }

  public searchHistoryElementSelected(searchEl: any): void {
    this.inputValue = searchEl.display_name;
    this.searchChanged.emit(searchEl);

    if (!this.routeIcon && !this.addressSearch && searchEl?.lat != null && (searchEl.lon != null || searchEl.lng != null)) {
      const lng = searchEl.lon ?? searchEl.lng;
      this.mapService.centerMap(15, searchEl.lat, lng);
      this.mapPoiDrawerService.drawPoi(searchEl.lat, lng, { address: searchEl.display_name });
    }
  }

  public homeShortcutSelected(): void {
    this.addressShortcutSelected(this.settingsService.getHomeAddress());
  }

  public workShortcutSelected(): void {
    this.addressShortcutSelected(this.settingsService.getWorkAddress());
  }

  private addressShortcutSelected(address: Address | undefined): void {
    // Not logged in: prompt sign in. Logged in but no saved address: send the
    // user to settings. Otherwise treat it like selecting a searched location.
    if (!this.isAuthorized()) {
      this.openAccount();
      return;
    }

    if (address?.Description) {
      this.searchHistoryElementSelected({
        display_name: address.Description,
        lat: address.Location.lat,
        lon: address.Location.lng,
        boundingbox: address.BoundingBox
      });
    } else {
      this.openSettings();
    }
  }

  private openAccount(): void {
    this.stackedModalsService.openModal(AccountDialogComponent, {
      data: { process: 'signIn' },
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  private openSettings(): void {
    this.stackedModalsService.openModal(SettingsDialogComponent,
      { panelClass: 'dialog-snap-to-fullscreen', disableClose: true, autoFocus: false });
  }

  private storeLastSearch(selected: SearchedRouteElement): void {
    // Store last 5 searches in localStorage
    const lastSearches = storeLastSearch(this.getLastSearches(), selected, Date.now());
    localStorage.setItem(LAST_SEARCHED_KEY, JSON.stringify(lastSearches));
    this.searchHistory.set(lastSearches);
    this.syncSearchHistory();
  }

  /**
   * Inside the native app the list is shared with its car search screen: ours is merged into the
   * native one (newest first, one entry per place) and the merged list replaces ours. Does nothing
   * anywhere else. Fire-and-forget; see docs/search-history-sync-ios.md.
   */
  private async syncSearchHistory(): Promise<void> {
    if (!this.webviewService.IsGPSTWebview()) {
      return;
    }
    const merged = syncSearchHistory(
      await this.webviewService.MergeSearchHistory(JSON.stringify(this.getLastSearches())));
    if (!merged) {
      return;
    }
    localStorage.setItem(LAST_SEARCHED_KEY, JSON.stringify(merged));
    this.searchHistory.set(merged);
  }

  private getLastSearches(): StoredSearch[] {
    let lastSearches: StoredSearch[] = [];
    try {
      const stored = localStorage.getItem(LAST_SEARCHED_KEY);
      if (stored) {
        lastSearches = JSON.parse(stored);
      }
    } catch {
      // Corrupt or unreadable stored history: fall back to an empty list.
      lastSearches = [];
    }
    return lastSearches;
  }

  public goBack(): void {
    this.goBackEvent.emit();
  }

  public getTextSegments(searchEl: any): TextSegment[] {
    return getTextSegments(searchEl);
  }
}
